import type { Organization, PaginatedList } from '@local/common-lib';
import { Person as NewPersonIcon } from '@mui/icons-material';
import {
  Button,
  CardActions,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { NavLink } from '../../components/nav-link/nav-link.js';
import {
  CenterCard,
  CenterLayout,
} from '../../components/page-layout/index.js';
import { useHistoryState } from '../../hooks/use-history-state.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';

export const OrganizationListPage: FC = () => {
  useSetTitle('Organizations');
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useAuthenticated();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useHistoryState('pagination', {
    limit: 12,
    skip: 0,
  });
  const [persons, setPersons] = useState<PaginatedList<Organization.Type>>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    auth.axios
      .get<PaginatedList<Organization.Type>>('/api/v1/organizations', {
        params: { ...pagination, sort: 'name' },
      })
      .then((response) => setPersons(response.data))
      .then(() => setLoading(false))
      .catch(console.log);
  }, [pagination]);

  if (!persons) {
    return <MiddleSpinner />;
  }

  return (
    <>
      <CenterCard>
        <CardActions sx={{ justifyContent: 'end', mx: 1 }}>
          <Button
            color="primary"
            component={NavLink}
            startIcon={<NewPersonIcon />}
            to="/organization/new"
          >
            New Organization
          </Button>
        </CardActions>
        {smallScreen ? (
          <NarrowPersonList persons={persons} />
        ) : (
          <WidePersonList persons={persons} />
        )}
      </CenterCard>

      {persons.matches > pagination.limit ? (
        <CenterLayout>
          <Pagination
            count={Math.ceil(persons.matches / pagination.limit)}
            disabled={loading}
            onChange={(_, page) => {
              setLoading(true);
              setPagination({
                limit: pagination.limit,
                skip: pagination.limit * (page - 1),
              });
            }}
            page={Math.floor(pagination.skip / pagination.limit) + 1}
            showFirstButton
            showLastButton
            sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
          />
        </CenterLayout>
      ) : undefined}
    </>
  );
};

const NarrowPersonList: FC<{ persons: PaginatedList<Organization.Type> }> = ({
  persons,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {persons.items.map((row) => (
          <TableRow
            key={row._id}
            onClick={() => navigate(`/organization/${row._id}`)}
            sx={{
              '&:last-child td, &:last-child th': { border: 0 },
              cursor: 'pointer',
            }}
          >
            <TableCell>
              <span>{row.name}</span>{' '}
              <span style={{ color: theme.palette.text.disabled }}>
                ({row.code})
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const WidePersonList: FC<{ persons: PaginatedList<Organization.Type> }> = ({
  persons,
}) => {
  const navigate = useNavigate();

  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>Code</TableCell>
          <TableCell>Name</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {persons.items.map((row) => (
          <TableRow
            key={row._id}
            onClick={() => navigate(`/organization/${row._id}`)}
            sx={{
              '&:last-child td, &:last-child th': { border: 0 },
              cursor: 'pointer',
            }}
          >
            <TableCell>{row.code}</TableCell>
            <TableCell>{row.name}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
